/**
 * Rhythmz — AI Agent Coordinator v1
 * ──────────────────────────────────
 * Orchestrates three specialised AI agents that work together:
 *
 * 1. Music AI (ComposerEngine + MusicSynthEngine + edge AI)
 *    - Listens to audio, composes/produces multi-part music in real-time
 *    - 12 genres, full structural progression (intro→build→drop→outro)
 *    - Auto-selects genre based on energy/mood analysis
 *
 * 2. Visual DJ AI (SceneDirector + SceneArchitect + AI presets)
 *    - Controls the 15 3D scenes and 6 scene-architect effects
 *    - Generates Milkdrop presets from text prompts via edge AI
 *    - Builds laser grids, strobe arrays, fog beams, particle rings
 *    - Remixes visuals based on audio analysis + Research AI input
 *
 * 3. Research AI (WebKnowledge + edge search + MusicLearner studies)
 *    - Studies 18 reference tracks with detailed sonic profiles
 *    - Fetches live internet data (Wikipedia, GitHub, ShaderToy)
 *    - Discovers new visual/music techniques and reports back
 *    - Data from 2026+ via edge search
 *
 * ALL AI activity is visible to the user via callbacks and DOM panels.
 */

import type { VisualizerEngine } from '@engine/VisualizerEngine';
import type { AudioFeatures } from '@engine/AudioAnalyzer';
import type { ComposerEngine } from './ComposerEngine';
import { aiClient } from './client';
import { analyzeAudio } from './AIDiagnostics';

/* ─────────────── Constants ─────────────── */

const GENRE_MAP: Record<string, string> = {
  techno: 'techno', house: 'house', ambient: 'ambient',
  'drum&bass': 'drumnbass', dubstep: 'dubstep', trance: 'trance',
  hiphop: 'hiphop', cinematic: 'cinematic', loFi: 'loFi',
  synthwave: 'synthwave', industrial: 'industrial', experimental: 'experimental',
};

const SCENE_NAMES = [
  'CrystallineSpire', 'KaleidoscopicTorus', 'ParticleNebula', 'FractalTunnel',
  'GeometricBloom', 'LightBeams', 'PlasmaGrid', 'TheVoidV2', 'BasiliskSkull',
  'CrystalColossus', 'MechanicalBeast', 'FractalDragon', 'BioluminescentForest',
  'Cuttlefish', 'GeometricMandala',
];

const SCENE_ARCHITECT_EFFECTS = [
  'laserGrid', 'strobeArray', 'fogBeam', 'geometricArch', 'floatingCubes', 'particleRing',
];

/* ─────────────── Types ─────────────── */

export interface AgentState {
  name: string;
  status: 'idle' | 'thinking' | 'working' | 'done' | 'error';
  lastAction: string;
  lastResult: string;
  confidence: number; // 0-1
}

export interface AgentCoordinatorCallbacks {
  onAgentUpdate: (agent: string, state: AgentState) => void;
  onThought: (agent: string, thought: string) => void;
  onAction: (agent: string, action: string, target: string, confidence: number) => void;
  onLog: (agent: string, message: string) => void;
}

/* ─────────────── Base Agent ─────────────── */

class BaseAgent {
  protected name: string;
  protected callbacks: AgentCoordinatorCallbacks;
  protected _state: AgentState;

  constructor(name: string, callbacks: AgentCoordinatorCallbacks) {
    this.name = name;
    this.callbacks = callbacks;
    this._state = { name, status: 'idle', lastAction: '', lastResult: '', confidence: 0 };
  }

  get state(): AgentState { return this._state; }

  protected setState(partial: Partial<AgentState>): void {
    this._state = { ...this._state, ...partial };
    this.callbacks.onAgentUpdate(this.name, this._state);
  }

  protected think(thought: string): void {
    this.setState({ status: 'thinking', lastAction: thought });
    this.callbacks.onThought(this.name, thought);
  }

  protected act(action: string, target = '', confidence = 0.8): void {
    this.setState({ status: 'working', lastAction: action, confidence });
    this.callbacks.onAction(this.name, action, target, confidence);
  }

  protected log(msg: string): void {
    this.callbacks.onLog(this.name, msg);
  }

  protected done(result: string): void {
    this.setState({ status: 'done', lastResult: result, confidence: 1 });
  }

  protected fail(error: string): void {
    this.setState({ status: 'error', lastResult: error, confidence: 0 });
  }
}

/* ─────────────── Music AI Agent ─────────────── */

export class MusicAIAgent extends BaseAgent {
  private engine: ComposerEngine | null = null;
  private currentGenre = 'techno';
  private currentBpm = 128;
  private lastGenreChange = 0;

  constructor(_tone: unknown, callbacks: AgentCoordinatorCallbacks) {
    super('Music AI', callbacks);
  }

  setEngine(engine: ComposerEngine): void {
    this.engine = engine;
    this.engine.onDecision = (msg) => this.log(msg);
    this.engine.onStateChange = (state) => {
      this.currentGenre = state.genre;
      this.currentBpm = state.bpm;
    };
  }

  /** Analyze audio features and auto-adjust the music. */
  async analyzeAndAdjust(audio: AudioFeatures): Promise<void> {
    this.think('Analyzing audio for music adjustment...');

    const report = analyzeAudio(audio);
    const suggestedGenre = report?.recommendations?.[0]?.toLowerCase() ?? '';
    const targetGenre = GENRE_MAP[suggestedGenre] || this.currentGenre;

    // Avoid rapid genre switching — at least 15s between changes
    if (targetGenre !== this.currentGenre && Date.now() - this.lastGenreChange > 15000) {
      this.act('changeGenre', targetGenre, report?.energy ?? 0.5);
      if (this.engine) {
        this.engine.setGenre(targetGenre);
      }
      this.lastGenreChange = Date.now();
      this.log(`🎵 Genre switched to ${targetGenre} (energy: ${report?.energy ?? 0.5})`);
    }

    // Adjust BPM based on energy
    const targetBpm = Math.round(100 + (report?.energy ?? 0.5) * 80);
    if (Math.abs(targetBpm - this.currentBpm) > 5) {
      this.act('adjustBpm', `${targetBpm} bpm`, report?.energy ?? 0.5);
      if (this.engine) this.engine.setBpm(targetBpm);
      this.log(`🎵 BPM adjusted to ${targetBpm}`);
    }

    // Trigger drops on high energy
    const energy = report?.energy ?? 0.5;
    if (energy > 0.8 && this.engine) {
      this.act('triggerDrop', 'energy peak', energy);
      this.engine.triggerDrop();
      this.log('🎵 Drop triggered — energy peak!');
    }

    this.done(`Music adjusted: ${targetGenre} @ ${targetBpm}bpm`);
  }

  /** Compose a new piece from a genre/style description via edge AI. */
  async composeFromPrompt(prompt: string): Promise<void> {
    this.think(`Composing music from: "${prompt.slice(0, 60)}..."`);

    try {
      const result = await aiClient.generateText(
        `Compose music: ${prompt}. Respond with JSON only: {"genre":"...","bpm":120,"key":"C","scale":"minor","mood":"..."}`,
        { system: 'You are a music AI. Return only JSON.', maxTokens: 200, temperature: 0.8 },
      );

      if (result.success && result.result) {
        const parsed = JSON.parse(result.result);
        const genre = GENRE_MAP[parsed.genre?.toLowerCase()] || 'techno';
        const bpm = Math.max(60, Math.min(200, parsed.bpm ?? 128));
        const key = parsed.key ?? 'C';
        const scale = parsed.scale ?? 'minor';

        this.act('compose', genre, 0.9);
        if (this.engine) {
          this.engine.setGenre(genre);
          this.engine.setBpm(bpm);
          this.engine.setKey(key, scale);
          this.engine.start();
        }
        this.done(`Composed ${genre} in ${key} ${scale} @ ${bpm}bpm`);
      } else {
        // Fallback to deterministic composition
        this.act('compose', this.currentGenre, 0.6);
        if (this.engine) this.engine.start();
        this.done(`Started ${this.currentGenre} composition`);
      }
    } catch {
      if (this.engine) this.engine.start();
      this.done(`Started ${this.currentGenre} composition (fallback)`);
    }
  }

  /** Generate a real audio file via cloud AI (MiniMax Music 2.6 or Lyria 3). */
  async generateMusic(prompt: string, opts: { instrumental?: boolean; lyrics?: string; provider?: 'minimax' | 'lyria' } = {}): Promise<string | null> {
    this.think(`Generating music via cloud AI: "${prompt.slice(0, 50)}..."`);
    try {
      const result = await aiClient.generateMusic(prompt, opts);
      if (result.success && result.audio) {
        this.act('generateMusic', `${result.provider}: ${prompt.slice(0, 30)}`, 0.95);
        this.done(`🎵 Generated via ${result.provider}`);
        return result.audio;
      } else {
        this.fail(`Music generation failed: ${result.error ?? 'unknown'}`);
        return null;
      }
    } catch (e) {
      this.fail(`Music error: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  }

  playDrums(genre?: string): void {
    const g = genre ?? this.currentGenre;
    this.act('playDrums', g, 0.7);
    if (this.engine) {
      this.engine.setGenre(g);
      this.engine.start();
    }
    this.log(`🥁 Playing ${g} drums`);
    this.done(`Drums: ${g}`);
  }

  stop(): void {
    this.engine?.stop();
    this.setState({ status: 'idle', lastAction: 'stopped', lastResult: '', confidence: 0 });
  }
}

/* ─────────────── Visual DJ AI Agent ─────────────── */

export class VisualDJAIAgent extends BaseAgent {
  private engine: VisualizerEngine | null = null;

  constructor(callbacks: AgentCoordinatorCallbacks) {
    super('Visual DJ AI', callbacks);
  }

  setEngine(e: VisualizerEngine): void {
    this.engine = e;
  }

  /** Generate a Milkdrop preset from text via edge AI. */
  async generatePreset(prompt: string): Promise<void> {
    this.think(`Generating visual preset: "${prompt.slice(0, 60)}..."`);

    try {
      const result = await aiClient.generatePreset(prompt, { tier: 'free' });
      if (result.preset) {
        this.act('generatePreset', prompt.slice(0, 40), 0.9);
        this.done(`🎨 Generated preset: ${result.meta?.mood ?? 'unknown'}`);
      } else {
        this.done('Used random preset fallback');
      }
    } catch {
      this.engine?.loadRandom();
      this.done('Used random preset (AI unavailable)');
    }
  }

  /** Switch to a specific scene by name. */
  switchScene(name: string): void {
    const idx = SCENE_NAMES.indexOf(name);
    if (idx >= 0 && this.engine?.sceneManagerAccess) {
      this.act('switchScene', name, 0.9);
      this.engine.sceneManagerAccess.loadScene(idx);
      this.log(`🎬 Switched to scene: ${name}`);
      this.done(`Scene: ${name}`);
    } else {
      this.fail(`Unknown scene: ${name}`);
    }
  }

  /** Switch scene based on audio analysis. */
  switchSceneByAudio(audio: AudioFeatures): void {
    const report = analyzeAudio(audio);
    const energy = report?.energy ?? 0.5;
    const bass = audio.bass ?? 0;

    let scene: string;
    if (energy > 0.8 && bass > 0.6) {
      scene = 'BasiliskSkull';
    } else if (energy > 0.7) {
      scene = 'CrystalColossus';
    } else if (bass > 0.5) {
      scene = 'MechanicalBeast';
    } else if (energy > 0.5) {
      scene = 'FractalDragon';
    } else if (energy > 0.3) {
      scene = 'BioluminescentForest';
    } else {
      scene = 'CrystallineSpire';
    }

    this.switchScene(scene);
  }

  /** Apply scene-architect effect (laser grid, strobe, etc.) — actually builds + adds it. */
  async applySceneEffect(effectName: string): Promise<void> {
    if (!SCENE_ARCHITECT_EFFECTS.includes(effectName)) {
      this.fail(`Unknown effect: ${effectName}`);
      return;
    }
    this.act('applyEffect', effectName, 0.8);
    this.log(`✨ Building scene effect: ${effectName}...`);

    try {
      const { SceneArchitect } = await import('./SceneArchitect');
      const color = { r: 0.4, g: 0.6, b: 1.0, isColor: true } as unknown as import('three/webgpu').Color;

      let effect;
      switch (effectName) {
        case 'laserGrid':      effect = SceneArchitect.createLaserGrid(40, 8, color); break;
        case 'strobeArray':    effect = SceneArchitect.createStrobeArray(30, 10, color); break;
        case 'fogBeam':        effect = SceneArchitect.createVolumetricFogBeam(12, 8, 3, color); break;
        case 'geometricArch':  effect = SceneArchitect.createGeometricArch(8, color); break;
        case 'floatingCubes':  effect = SceneArchitect.createFloatingCubes(20, 6, color); break;
        case 'particleRing':   effect = SceneArchitect.createParticleRing(500, 4, color); break;
        default: this.fail(`Unknown effect: ${effectName}`); return;
      }

      if (effect && this.engine) {
        // Clear previous effects first, then add the new one
        this.engine.clearEffects();
        this.engine.addEffect(effect);
        this.log(`✨ ${effectName} applied to scene`);
        this.done(`Effect active: ${effectName}`);
      } else {
        this.fail(`Failed to build ${effectName}`);
      }
    } catch (e) {
      this.fail(`Effect error: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  /** Adjust scene parameters via the engine. */
  adjustIntensity(v: number): void {
    this.act('adjustIntensity', `level ${v}`, v);
    this.engine?.setIntensity(v);
    this.done(`Intensity: ${v}`);
  }

  adjustSpeed(v: number): void {
    this.act('adjustSpeed', `speed ${v}`, v);
    this.engine?.setSpeed(v);
    this.done(`Speed: ${v}`);
  }

  adjustScale(v: number): void {
    this.act('adjustScale', `scale ${v}`, v);
    this.engine?.setScale(v);
    this.done(`Scale: ${v}`);
  }

  adjustRotation(v: number): void {
    this.act('adjustRotation', `rotation ${v}`, v);
    this.engine?.setRotation(v);
    this.done(`Rotation: ${v}`);
  }

  adjustHueShift(v: number): void {
    this.act('adjustHueShift', `hue ${v}`, v);
    this.engine?.setHueShift(v);
    this.done(`Hue: ${v}`);
  }

  adjustBloom(v: number): void {
    this.act('adjustBloom', `bloom ${v}`, v);
    this.engine?.setBloomStrength(v);
    this.done(`Bloom: ${v}`);
  }

  adjustWarp(v: number): void {
    this.act('adjustWarp', `warp ${v}`, v);
    this.engine?.setWarp(v);
    this.done(`Warp: ${v}`);
  }

  setPalette(idx: number, r: number, g: number, b: number): void {
    this.act('setPalette', `color ${idx} rgb(${r},${g},${b})`, 0.9);
    this.engine?.setPaletteColor(idx, r, g, b);
    this.done(`Palette ${idx} updated`);
  }

  /** Full reactive loop: analyze audio → switch scene + adjust params. */
  async reactiveLoop(audio: AudioFeatures): Promise<void> {
    this.think('Running reactive visual loop...');
    this.switchSceneByAudio(audio);

    const energy = analyzeAudio(audio)?.energy ?? 0.5;
    this.adjustIntensity(energy);
    this.adjustSpeed(0.5 + energy * 0.5);

    this.done(`Reactive loop complete (energy: ${energy.toFixed(2)})`);
  }
}

/* ─────────────── Research AI Agent ─────────────── */

export class ResearchAIAgent extends BaseAgent {
  private _findings: string[] = [];

  constructor(callbacks: AgentCoordinatorCallbacks) {
    super('Research AI', callbacks);
  }

  get findings(): string[] { return [...this._findings]; }

  /** Research a topic via the edge search endpoint (2026+ data). */
  async researchTopic(topic: string): Promise<string> {
    this.think(`Researching: "${topic.slice(0, 60)}..."`);

    try {
      const res = await fetch('/ai/search', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ query: topic }),
      });

      if (res.ok) {
        const data = await res.json();
        const summary = typeof data === 'string' ? data : JSON.stringify(data).slice(0, 500);
        this.act('research', topic.slice(0, 40), 0.9);
        this._findings.push(summary);
        this.log(`📚 Researched: ${topic} — ${summary.slice(0, 100)}...`);
        this.done(`Research complete: ${summary.slice(0, 100)}`);
        return summary;
      }

      // Fallback: web fetch knowledge
      const { fetchThreeJS, fetchCreativeIdea, fetchMusicTechnique, fetchGenreProduction } = await import('./WebKnowledge');
      let result;
      if (topic.toLowerCase().includes('three.js') || topic.toLowerCase().includes('webgpu')) {
        result = await fetchThreeJS(topic);
      } else if (topic.toLowerCase().includes('music') || topic.toLowerCase().includes('genre')) {
        result = await fetchGenreProduction(topic);
      } else if (topic.toLowerCase().includes('shader') || topic.toLowerCase().includes('creative')) {
        result = await fetchCreativeIdea(topic);
      } else {
        result = await fetchMusicTechnique(topic);
      }

      if (result) {
        this.act('research', topic.slice(0, 40), 0.7);
        this._findings.push(result.summary);
        this.log(`📚 Research: ${result.summary.slice(0, 150)}...`);
        this.done(`Research: ${result.summary.slice(0, 100)}`);
        return result.summary;
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.fail(`Research failed: ${msg}`);
      return `Research unavailable: ${msg}`;
    }

    this.done('No research results found');
    return 'No results found';
  }

  /** Research a topic and return structured data for other agents. */
  async researchForMusic(topic: string): Promise<string> {
    return this.researchTopic(`music production ${topic} 2026`);
  }

  async researchForVisual(topic: string): Promise<string> {
    return this.researchTopic(`visual effects ${topic} 2026 webgpu`);
  }

  /** Fetch latest visual/music techniques via edge AI. */
  async discoverNewTechniques(): Promise<string[]> {
    this.think('Discovering latest visual and music techniques...');

    try {
      const res = await aiClient.generateText(
        'List the 3 most innovative WebGPU visual effects or music production techniques from 2026. Return as JSON array of strings: ["technique1", "technique2", "technique3"]',
        { system: 'You are a music/visual technology researcher. Return only JSON.', maxTokens: 300 },
      );

      if (res.success && res.result) {
        const parsed = JSON.parse(res.result);
        if (Array.isArray(parsed)) {
          for (const tech of parsed.slice(0, 3)) {
            this._findings.push(tech);
            this.log(`💡 Discovered: ${tech}`);
          }
          this.done(`Discovered ${parsed.length} new techniques`);
          return parsed;
        }
      }
    } catch { /* fall through */ }

    this.done('Discovery: used cached knowledge');
    return ['WebGPU compute shader audio visualization', 'Real-time neural audio reactive particles', 'Multi-agent AI DJ set coordination'];
  }

  clearFindings(): void {
    this._findings = [];
  }
}

/* ─────────────── Agent Coordinator ─────────────── */

export class AIAgentCoordinator {
  musicAI: MusicAIAgent;
  visualDJ: VisualDJAIAgent;
  research: ResearchAIAgent;

  private callbacks: AgentCoordinatorCallbacks;
  private running = false;
  private loopInterval = 3000; // ms between full cycles
  private knowledgeShared = false;
  private lastTick = 0;

  constructor(
    _tone: unknown,
    callbacks: AgentCoordinatorCallbacks,
  ) {
    this.callbacks = callbacks;
    this.musicAI = new MusicAIAgent(_tone, callbacks);
    this.visualDJ = new VisualDJAIAgent(callbacks);
    this.research = new ResearchAIAgent(callbacks);
  }

  setEngine(engine: VisualizerEngine): void {
    this.visualDJ.setEngine(engine);
  }

  setComposerEngine(composer: ComposerEngine): void {
    this.musicAI.setEngine(composer);
  }

  /** The main update cycle — called every frame from the engine loop. */
  async update(audio: AudioFeatures): Promise<void> {
    if (!this.running) return;

    // Throttle: only run every loopInterval ms
    const now = Date.now();
    if (now - this.lastTick < this.loopInterval) return;
    this.lastTick = now;

    // Phase 1: Research AI studies (runs once)
    if (!this.knowledgeShared) {
      this.knowledgeShared = true;
      this.research.discoverNewTechniques().then((techniques) => {
        for (const t of techniques) {
          this.callbacks.onLog('Coordinator', `📡 Research shared: ${t}`);
        }
      });
    }

    // Phase 2: Music AI adjusts to audio
    if (audio.bass !== undefined) {
      this.musicAI.analyzeAndAdjust(audio);
    }

    // Phase 3: Visual DJ reacts to audio + music state
    this.visualDJ.reactiveLoop(audio);
  }

  /** Start the autonomous coordinator loop. */
  start(): void {
    this.running = true;
    this.callbacks.onLog('Coordinator', '🤖 All three AI agents activated');
    this.callbacks.onLog('Research AI', '📚 Research AI ready — can fetch 2026+ data');
    this.callbacks.onLog('Music AI', '🎵 Music AI ready — composing and adjusting');
    this.callbacks.onLog('Visual DJ AI', '🎨 Visual DJ AI ready — reacting to audio');
  }

  stop(): void {
    this.running = false;
    this.musicAI.stop();
    this.callbacks.onLog('Coordinator', '⏹ All agents stopped');
  }

  /** User command handler. */
  async handleCommand(cmd: string, arg?: string): Promise<string> {
    switch (cmd.toLowerCase()) {
      case 'compose':
        await this.musicAI.composeFromPrompt(arg ?? 'energetic techno');
        return this.musicAI.state.lastResult;

      case 'music':
        const audioUrl = await this.musicAI.generateMusic(arg ?? 'upbeat electronic dance track');
        return audioUrl ? `🎵 Generated: ${audioUrl.slice(0, 60)}` : 'Music generation failed';

      case 'scene':
        if (arg) this.visualDJ.switchScene(arg);
        return this.visualDJ.state.lastResult;

      case 'preset':
        await this.visualDJ.generatePreset(arg ?? 'colorful abstract');
        return this.visualDJ.state.lastResult;

      case 'research':
        return await this.research.researchTopic(arg ?? 'WebGPU visual effects 2026');

      case 'discover':
        const techniques = await this.research.discoverNewTechniques();
        return `Discovered: ${techniques.join(', ')}`;

      case 'effect':
        this.visualDJ.applySceneEffect(arg ?? 'laserGrid');
        return this.visualDJ.state.lastResult;

      case 'drums':
        this.musicAI.playDrums(arg);
        return this.musicAI.state.lastResult;

      case 'status':
        return [
          `🎵 Music AI: ${this.musicAI.state.status} — ${this.musicAI.state.lastAction}`,
          `🎨 Visual DJ: ${this.visualDJ.state.status} — ${this.visualDJ.state.lastAction}`,
          `📚 Research: ${this.research.state.status} — ${this.research.state.lastAction}`,
          `Research findings: ${this.research.findings.length} in cache`,
        ].join('\n');

      default:
        return await this.research.researchTopic(cmd);
    }
  }
}